import type {
    AccommodationDescriptionFilterFilterType,
    AccommodationFilterType,
    ActivityDescriptionFilterType,
    ActivityFilterType,
    BaseFilterType,
    CarHireFilterType,
    CharterFilterType,
    CharterScheduleType,
    CruiseFilterType,
    FlightNumberType,
    InfoCatalogueType,
    InfoGuestCategoryType,
    InsuranceFilterType,
    ShipFilterType,
    TransferFilterType,
} from '~/utils/types/account/products/types'

const initBaseContract = () => ({
    name: {},
    active: true,
    ...(runtimeStore.getCRMOption('asp.option.show.product.publishing') && authStore.isTO1 && {stage: 'DRAFT'}),
    internalDescription: null,
})

const initBasFilter = (): BaseFilterType => ({
    name: null,
    supplierId: null,
    active: true,
    stage: null,
    departmentId: null,
})

export const initCharterFilter = (): CharterFilterType => ({
    ...initBasFilter(),
    countryIdFrom: null,
    cityIdFrom: null,
    countryIdTo: null,
    cityIdTo: null,
    supplierName: null,
})

export const initInfoGuestCategory = (): InfoGuestCategoryType => ({
    name: {},
    description: {},
    active: true,
    isDefault: false,
    category: 'ADULTS',
    ageFrom: null,
    ageTo: null,
})

export const initInsuranceService = () => ({
    name: {},
    description: {},
    active: true,
    amountCoverage: null,
    franchice: null,
    currencyCode: null,
})

export const initFlightNumber = (): FlightNumberType => ({
    name: {},
    categoryDescription: null,
    direction: 'OUTBOUND',
    apiOwnCharterServiceCategories: [],
    active: true,
})

export const initCharterSchedule = (): CharterScheduleType => ({
    active: true,
    duration: null,
    endDate: null,
    startTime: null,
    categoryId: null,
})

export const initCharterExtraServices = () => ({
    name: {},
    extraServiceCategory: null,
    amount: null,
    unitOfMeasure: null,
    limit: null,
    unitPricingType: 'PER_SERVICE',
    enabledForPaxes: [],
    providerType: 'SUPPLIER',
    enabledAllForServices: true,
    description: {},
    active: true,
    enabledForServiceCategories: [],
    forAllPaxes: true,
})

export const initCharterContract = () => ({
    ...initBaseContract(),
    productName: null,
    airlineCode: null,
    supplierId: null,
    departureCharterLocation: {
        airportCode: null,
    },
    destinationCharterLocation: {
        airportCode: null,
    },
    visibleInReservation: true,
})

export const initHotelContract = () => ({
    ...initBaseContract(),
    allowToBookForChildrenWithoutAdults: false,
    productName: null,
    supplierId: null,
    theSameSecondaryServiceForAll: false,
    visibleInReservation: true,
    hotelContentId: null,
})

export const initHotelInfo = () => ({
    hotelName: {},
    supplierId: 1,
    hotelCode: null,
    postalCode: null,
    countryId: null,
    cityId: null,
    address: {},
    hotelChainId: null,
    districtId: null,
    hotelTypeId: null,
    categoryId: null,
    groupNumber: null,
    giataId: null,
    phone: null,
    phone2: null,
    phone3: null,
    fax: null,
    url: null,
    email: null,
    email2: null,
    email3: null,
    fullLegalNameLocalizable: {},
    registrationID: null,
    taxIdentificationNumber: null,
})

export const initRoomType = () => ({
    name: {},
    active: true,
    roomCode: null,
    otaCode: '',
    roomCategoryId: null,
    shortDescription: {},
    description: {},
    beds: [],
    maxOccupancy: null,
    minOccupancy: null,
    maxExtraPlace: null,
    maxWithoutPlace: null,
    minTotalOccupancy: null,
    maxTotalOccupancy: null,
    bedroomsCount: null,
    bathroomsCount: null,
    allocationOptions: [],
    roomAmenityEnabled: {},
    channelManagerObjects: [],
    roomType: 'STANDARD',
    sustainabilityLevel: 'BASIC',
})

export const initExcursionContract = () => ({
    ...initBaseContract(),
    productName: null,
    supplierId: null,
    variantId: null,
    sharingType: 'SHARED',
    transportationType: 'WALKING',
    singleDay: true,
    duration: null,
    departureLocations: [],
    destinationLocations: [],
    visibleInReservation: true,
    recommended: false,
})

export const initEventContract = () => ({
    ...initBaseContract(),
    productName: null,
    supplierId: null,
    variantId: null,
    eventType: 'CONCERT',
    sharingType: 'SHARED',
    countryId: null,
    cityId: null,
    visibleInReservation: true,
    recommended: false,
})

export const initTransferContract = () => ({
    ...initBaseContract(),
    productName: null,
    supplierId: null,
    variantId: null,
    transferType: 'SHARED',
    duration: null,
    departureLocations: [],
    destinationLocations: [],
    visibleInReservation: true,
})

export const initOwnExcursionContractLocation = () => ({
    name: {},
    type: 'HOTEL',
    countryId: null,
    cityId: null,
    timeShift: null,
    latitude: null,
    longitude: null,
})

export const initOwnTransferContractLocation = () => ({
    name: {},
    type: 'HOTEL',
    countryId: null,
    cityId: null,
    timeShift: null,
    latitude: null,
    longitude: null,
    locationCode: null,
})

export const initActivityFilter = (): ActivityFilterType => ({
    ...initBasFilter(),
    supplierName: null,
    countryId: null,
    cityId: null,
    category: null,
    sharingType: null,
    variantId: null,
})

export const initActivityDescriptionFilter = (): ActivityDescriptionFilterType => ({
    pattern: null,
    supplierId: null,
    countryId: null,
    cityId: null,
    active: true,
    variantId: null,
})

export const initAccommodationFilter = (): AccommodationFilterType => ({
    ...initBasFilter(),
    supplierName: null,
    countryId: null,
    cityId: null,
    internalDescription: null,
})

export const initAccommodationDescriptionFilter = (): AccommodationDescriptionFilterFilterType => ({
    name: null,
    hotelCode: null,
    countryId: null,
    cityId: null,
    active: true,
    ratingFrom: null,
    ratingTo: null,
    contentProviderId: 1,
    groupId: null,
    category: null,
})

const initBaseExtraServiceData = () => ({
    active: true,
    name: {},
    description: {},
})

export const initExcursionExtraServices = () => ({
    ...initBaseExtraServiceData(),
    unitPricingType: 'PER_UNIT',
    enabledAllForServices: true,
    enabledForServices: [],
    forAllPaxes: true,
})

export const initTransferExtraServices = () => ({
    ...initBaseExtraServiceData(),
    unitPricingType: 'PER_UNIT',
    providerType: 'HOTEL',
    enabledAllForServices: true,
    enabledForServices: [],
    categoryId: null,
    forAllPaxes: true,
})

export const initEventExtraServices = () => ({
    ...initBaseExtraServiceData(),
    unitPricingType: 'PER_UNIT',
    calculationType: 'BY_SERVICE',
    providerType: 'HOTEL',
    enabledForPaxes: [],
    enabledAllForServices: true,
    enabledForServices: [],
    categoryId: null,
    forAllPaxes: true,
})

export const initInfoCatalogue = (): InfoCatalogueType => ({
    name: {},
    active: true,
})

export const initBaseFormData = () => ({
    name: {},
    description: {},
    active: true,
})

export const initExcursionServiceFormData = () => ({
    name: {},
    description: {},
    active: true,
    minOccupancy: null,
    maxOccupancy: null,
    startTime: null,
    accompanimentInfos: [{languageCode: 0, type: 'OTHER'}],
})

export const initEventServiceFormData = () => ({
    name: {},
    description: {},
    active: true,
    minOccupancy: null,
    maxOccupancy: null,
    categoryId: null,
})

export const initCruiseAccommodationFormData = () => ({
    name: {},
    description: {},
    active: true,
    minOccupancy: 0,
    maxOccupancy: 0,
    minExtraplace: 0,
    maxExtraplace: 0,
    minWithoutPlace: 0,
    maxWithoutPlace: 0,
    minTotalOccupancy: 0,
    maxTotalOccupancy: 0,
    categoryId: null,
})

export const initTransferFilter = (): TransferFilterType => ({
    ...initBasFilter(),
    countryIdFrom: null,
    cityIdFrom: null,
    countryIdTo: null,
    cityIdTo: null,
    supplierName: null,
    variantId: null,
    sharingType: null,
})

export const initTransferServiceFormData = () => ({
    name: {},
    active: true,
    minOccupancy: null,
    maxOccupancy: null,
    startTime: null,
    startTimeTo: null,
    ownTransferServiceDriverDescriptions: [{languageCode: 'LOCAL'}],
    ownTransferServiceTime: 'ANY_TIME',
})

export const initCarHireFilter = (): CarHireFilterType => ({
    ...initBasFilter(),
    supplierName: null,
})

export const initInsuranceFilter = (): InsuranceFilterType => ({
    ...initBasFilter(),
    insuranceLocationsCountryId: null,
})

export const initCarHireExtraServices = () => ({
    name: {},
    description: {},
    categoryId: null,
    active: true,
    calculationType: 'BY_SERVICE',
    extraServiceApplicationType: 'ALWAYS',
    officeCombinations: [],
})

export const initCarHireContract = () => ({
    ...initBaseContract(),
    productName: null,
    supplierId: null,
    allOffices: true,
    officeIds: [],
    vehicleCategory: null,
    transmissionType: null,
    vehicleClass: null,
    fuelType: null,
    doorCount: null,
    baggageQuantity: null,
    passengerQuantity: null,
    features: [],
    visibleInReservation: true,
})

export const initCruiseContract = () => ({
    ...initBaseContract(),
    productName: null,
    supplierId: null,
    duration: null,
    theSameSecondaryServiceForAll: false,
    departureLocations: [{countryId: null, cityId: null}],
    destinationLocations: [],
    shipContentId: null,
    visibleInReservation: true,
})

export const initTourContract = () => ({
    ...initBaseContract(),
    supplierId: null,
    duration: null,
    theSameSecondaryServiceForAll: false,
    destinationLocations: [],
})

export const initPackageTourContract = () => ({
    ...initBaseContract(),
    productName: null,
    supplierId: null,
    duration: null,
    departureLocations: [],
    destinationLocations: [],
    programId: null,
    tags: null,
    priceConfiguration: 'DYNAMIC',
})

export const initInsuranceContract = () => ({
    ...initBaseContract(),
    supplierId: null,
    accident: false,
    notLeave: false,
    visibleInReservation: true,
    allLocations: true,
    insuranceLocations: [],
    allCitizenships: true,
    citizenships: [],
})

export const initVisaContract = () => ({
    ...initBaseContract(),
    supplierId: null,
    countryId: null,
    visibleInReservation: true,
    applyDocumentsPersonally: false,
    allResidences: true,
    residences: [],
})

export const initExtraServiceContract = () => ({
    ...initBaseContract(),
    productName: null,
    supplierId: null,
    unitPricingType: 'SERVICE',
    allLocations: true,
    locations: [],
    visibleInReservation: true,
})

export const initCruiseFilter = (): CruiseFilterType => ({
    ...initBasFilter(),
    supplierName: null,
    variantId: null,
})

export const initShipFilter = (): ShipFilterType => ({
    name: null,
    active: true,
    type: null,
})

export const initToursFilter = (): BaseFilterType => ({
    ...initBasFilter(),
})

export const initPackageToursFilter = () => ({
    ...initBasFilter(),
    programId: null,
})

export const initCruiseExtraServices = () => ({
    ...initBaseExtraServiceData(),
    unitPricingType: 'PER_UNIT',
    calculationType: 'BY_SERVICE',
    providerType: 'HOTEL',
    enabledForPaxes: [],
    enabledAllForServices: true,
    enabledForServices: [],
    categoryId: null,
    forAllPaxes: true,
})

export const initAccommodationExtraServices = () => ({
    ...initBaseExtraServiceData(),
    extraServiceCategoryName: null,
    unitPricingType: 'PER_UNIT',
    calculationType: 'BY_SERVICE',
    providerType: 'HOTEL',
    enabledForPaxes: [],
    forAllPaxes: true,
    enabledForServices: [],
    enabledAllForServices: true,
    enabledForSecondaryServices: [],
    forAllSecondaryServices: true,
    membershipLevels: [],
    forAllMembershipLevels: true,
    enabledForPlaceTypes: [],
    forAllPlaceTypes: true,
})

export const initVisaExtraServices = () => ({
    ...initBaseExtraServiceData(),
    unitPricingType: 'PER_UNIT',
    forAllPaxes: true,
    enabledForServices: [],
    enabledAllForServices: true,
})

export const initInfoSecondaryService = () => ({
    name: {},
    description: {},
    active: true,
    defaultService: false,
    serviceTypeId: null,
})

export const initInfoMealTypes = () => ({
    name: {},
    description: {},
    shortDescription: {},
    active: true,
    useAsDefault: false,
    type: null,
    mealCode: null,
})

export const initItinerary = () => ({
    name: null,
    productName: {},
    obligatoryStatus: 'REQUIRED',
    type: null,
    requestedTags: 'public',
    excludedTags: null,
    dayNumber: 1,
    nightsCount: null,
    daysCount: null,
    singleProductMode: true,
    externalSuppliersEnables: false,
    departureAirportCode: null,
    arrivalAirportCode: null,
    returnDayNumber: null,
    journeyType: null,
    productIds: [],
    cityId: null,
})

export const initIHotelGeneralDescription = () => ({
    rating: null,
    internalNote: {en: null},
    recommended: false,
    villaDescription: {
        buildYear: null,
        modernizedYear: null,
        buildingMaterial: null,
        territoryArea: null,
        houseArea: null,
        quantityFloors: null,
        numberOfPeople: null,
        quantityRooms: null,
        quantityBathRooms: null,
        quantityBars: null,
        quantitySingleRooms: null,
        quantityIndoorPools: null,
        quantityRestaurants: null,
        quantityDoubleRooms: null,
        quantityOutdoorPools: null,
        quantityTripleOrMoreRooms: null,
    },
    classifications: [],
})
